import { ApiOrderCancellation } from '../models/order.models';

/**
 * CWS-1779 / CWS-1781: the row actions menu on the Orders list, built from the
 * server's per-row `cancellation` flags rather than decided client-side.
 *
 * Every row offers the same three items — View details, Refund, Cancel — so
 * the menu never changes shape under the operator. What the server refuses is
 * greyed with the reason on hover (and, where the design draws it, inline):
 *
 *   completed single wash   Refund single wash · Cancel single wash (greyed: completed)
 *   active prepaid          Refund prepaid (greyed: nothing to refund yet) · Cancel prepaid
 *   active membership       Refund membership (greyed) · Cancel membership
 *   membership + add-on     Refund add-on · Cancel membership
 *   completed wash + add-on Refund single wash + add-on · Cancel … (greyed)
 *   refunded / spent        Refund (greyed: "Nothing left to refund") · Cancel (greyed)
 *
 * `available: false` (feature off, or the row could not be decided) falls back
 * to today's menu so nothing changes for a shop that has not been switched on.
 */

export type OrderRowActionKey = 'view' | 'refund' | 'cancel' | 'retry_refund';

export interface OrderRowAction {
  key: OrderRowActionKey;
  label: string;
  enabled: boolean;
  /** Why a disabled item is disabled; shown on hover. */
  hint?: string;
  /** Also render the hint under the label, as the design does for a spent row. */
  inlineHint?: boolean;
}

export interface OrderRowActionsInput {
  /** `orders.wash_type` */
  washTypeValue?: string | null;
  /** Add-on chips on the row (CWS-1125); any means the order carries an add-on line. */
  addOnLabels?: string[] | null;
  cancellation?: ApiOrderCancellation | null;
  /** The host's own rule (role, ownership). Applied on top of the server flags. */
  canCancel: boolean;
  /** Why the host's rule said no, for the hover hint. */
  hostCancelHint?: string;
  /** Legacy menu only: admin pages show Cancel even when disabled; staff pages hide it. */
  alwaysShowCancel: boolean;
}

const NOUN_BY_WASH_TYPE: Record<string, string> = {
  single_wash: 'single wash',
  monthly_membership: 'membership',
  prepaid_wash_book: 'prepaid',
  add_on: 'add-on',
  rewash_single_wash: 'rewash',
  rewash_membership: 'rewash',
  rewash_prepaid_redemption: 'rewash',
};

/** Why nothing can be actioned, by the server's reason code. */
const NONE_HINTS: Record<string, string> = {
  fully_refunded: 'Nothing left to refund',
  already_cancelled: 'Nothing left to refund',
  cancel_already_scheduled: 'Cancellation already scheduled',
  imported_membership: 'Managed by the vendor',
  family_plan: 'Billed to the primary member',
  rewash: 'A rewash has nothing to refund',
  not_paid: 'Not paid',
  unsupported_purchase_type: 'Not available for this purchase',
};

const HINT_REFUND_LIVE = 'Nothing to refund yet — cancel to refund an unused purchase';
const HINT_REFUND_UNPAID = 'Nothing was paid';
const HINT_CANCEL_COMPLETED = 'Completed items can only be refunded';
const HINT_CANCEL_SERVER = 'Cannot be cancelled';
const HINT_CANCEL_HOST = 'You can only cancel your own orders';
const HINT_REFUND_OWED = 'A refund is still owed on this order — retry it first';

/** "single wash", "prepaid + add-on", "membership" … */
export function orderItemNoun(
  input: Pick<OrderRowActionsInput, 'washTypeValue' | 'addOnLabels'>
): string {
  const base = NOUN_BY_WASH_TYPE[String(input.washTypeValue || '')] || 'order';
  const hasAddOn = Array.isArray(input.addOnLabels) && input.addOnLabels.length > 0;
  if (!hasAddOn || base === 'add-on' || base === 'membership') return base;
  return `${base} + add-on`;
}

function legacyActions(input: OrderRowActionsInput): OrderRowAction[] {
  const actions: OrderRowAction[] = [{ key: 'view', label: 'View details', enabled: true }];
  if (input.alwaysShowCancel || input.canCancel) {
    actions.push({ key: 'cancel', label: 'Cancel', enabled: input.canCancel });
  }
  return actions;
}

function view(): OrderRowAction {
  return { key: 'view', label: 'View details', enabled: true };
}

function disabled(
  key: OrderRowActionKey,
  label: string,
  hint: string,
  inlineHint = false
): OrderRowAction {
  return { key, label, enabled: false, hint, inlineHint };
}

/**
 * @returns The menu, top to bottom: View details, Refund, Cancel — always all three
 *   once the feature is on for the shop.
 */
export function buildOrderRowActions(input: OrderRowActionsInput): OrderRowAction[] {
  const flags = input.cancellation;
  if (!flags || flags.available !== true) {
    return legacyActions(input);
  }

  const noun = orderItemNoun(input);
  const isMembership = String(input.washTypeValue || '') === 'monthly_membership';
  const hostHint = input.hostCancelHint || HINT_CANCEL_HOST;

  // CWS-1779: the last cancellation on this row still owes money. Retry is the
  // only sensible next step; nothing else is offered until it has gone out.
  if (flags.refund_failed) {
    return [
      view(),
      { key: 'retry_refund', label: 'Retry refund', enabled: true },
      disabled('cancel', `Cancel ${noun}`, HINT_REFUND_OWED),
    ];
  }

  if (flags.action === 'none') {
    const reason = String(flags.reason || '');
    const hint = NONE_HINTS[reason] || 'Not available';
    // The design writes "Nothing left to refund" under a spent row; every other
    // reason stays on hover so a vendor-managed row reads as plainly greyed.
    const inline = reason === 'fully_refunded' || reason === 'already_cancelled';
    return [view(), disabled('refund', 'Refund', hint, inline), disabled('cancel', 'Cancel', hint)];
  }

  // A mixed order is the design's "membership + add-on": the live membership is
  // cancelled, the completed add-on refunded, and the labels say which is which.
  if (flags.action === 'mixed') {
    const cancelLabel = isMembership ? 'Cancel membership' : `Cancel ${noun}`;
    return [
      view(),
      flags.can_refund
        ? { key: 'refund', label: 'Refund add-on', enabled: true }
        : disabled('refund', 'Refund add-on', HINT_REFUND_UNPAID),
      cancelFor(cancelLabel, flags, input.canCancel, hostHint),
    ];
  }

  if (flags.action === 'refund') {
    return [
      view(),
      flags.can_refund
        ? { key: 'refund', label: `Refund ${noun}`, enabled: true }
        : disabled('refund', `Refund ${noun}`, HINT_REFUND_UNPAID),
      // Completed items can never be cancelled; the item stays visible and greyed.
      disabled('cancel', `Cancel ${noun}`, HINT_CANCEL_COMPLETED),
    ];
  }

  // action === 'cancel': a live item. Refund is the cancel's refund step, so
  // it is greyed here with a hint that says so — unless the server found
  // something refundable on its own (a partly refunded line beside a live one).
  return [
    view(),
    flags.can_refund
      ? { key: 'refund', label: `Refund ${noun}`, enabled: true }
      : disabled('refund', `Refund ${noun}`, HINT_REFUND_LIVE),
    cancelFor(`Cancel ${noun}`, flags, input.canCancel, hostHint),
  ];
}

function cancelFor(
  label: string,
  flags: ApiOrderCancellation,
  hostCanCancel: boolean,
  hostHint: string
): OrderRowAction {
  if (!flags.can_cancel) return disabled('cancel', label, HINT_CANCEL_SERVER);
  if (!hostCanCancel) return disabled('cancel', label, hostHint);
  return { key: 'cancel', label, enabled: true };
}
